/**
 * Typed API wrappers for the editable-pages endpoints.
 *
 * All functions unwrap the { error, data } envelope and throw on error,
 * so callers can use simple try/catch without re-checking response.error.
 *
 * Use makePageBuilderApi(base) to create wrappers bound to a different base
 * path (e.g. '/marketing/editable-pages' or '/seo/editable-pages').
 * The named exports below default to '/admin/editable-pages' and are preserved
 * unchanged so existing admin call sites need no modification.
 */

import api from '../../../utils/api';

// ─── Factory ─────────────────────────────────────────────────────────────────

/**
 * makePageBuilderApi(base)
 *
 * Returns an object with getPage / saveDraft / publish / unpublish bound to
 * the given base path. Use this to create per-role API instances:
 *
 *   const pbApi = makePageBuilderApi('/marketing/editable-pages');
 *   pbApi.getPage('join-us-as-library');
 *
 * @param {string} base - Base path prefix (no trailing slash).
 *   Default: '/admin/editable-pages'
 */
export function makePageBuilderApi(base = '/admin/editable-pages') {
  return {
    /**
     * GET /
     * Lists all pages under this base. Returns Array<{ id, slug, title, status, published_at, created_at, updated_at }>
     */
    async listPages() {
      const res = await api.get(base);
      if (res.data.error) throw new Error(res.data.message || 'Failed to list pages');
      return res.data.data;
    },

    /**
     * POST /
     * Creates a new page. Body: { slug, title, layout? }
     * Returns the created page object.
     * Throws with status 409 message on duplicate slug.
     */
    async createPage({ slug, title, layout = [] }) {
      const res = await api.post(base, { slug, title, layout });
      if (res.data.error) {
        const err = new Error(res.data.message || 'Failed to create page');
        err.status = res.status;
        throw err;
      }
      return res.data.data;
    },

    /**
     * DELETE /:slug
     * Deletes a page permanently. Returns { slug }.
     */
    async deletePage(slug) {
      const res = await api.delete(`${base}/${slug}`);
      if (res.data.error) throw new Error(res.data.message || 'Failed to delete page');
      return res.data.data;
    },

    /**
     * GET /:slug
     * Returns the full editing payload: { slug, title, status, layout, published_layout }
     */
    async getPage(slug) {
      const res = await api.get(`${base}/${slug}`);
      if (res.data.error) throw new Error(res.data.message || 'Failed to load page');
      return res.data.data;
    },

    /**
     * PUT /:slug
     * Saves a draft. Body: { title?, layout:Array }
     * Returns { slug, layout, status }
     * On 400 validation errors, throws an object with { message, errors:[] }
     */
    async saveDraft(slug, { title, layout }) {
      const res = await api.put(`${base}/${slug}`, { title, layout });
      if (res.data.error) {
        const err = new Error(res.data.message || 'Failed to save draft');
        err.errors = res.data.errors || [];
        throw err;
      }
      return res.data.data;
    },

    /**
     * POST /:slug/publish
     * Returns { slug, status, published_at }
     */
    async publish(slug) {
      const res = await api.post(`${base}/${slug}/publish`);
      if (res.data.error) throw new Error(res.data.message || 'Failed to publish');
      return res.data.data;
    },

    /**
     * POST /:slug/unpublish
     * Returns { slug, status, published_layout: null }
     */
    async unpublish(slug) {
      const res = await api.post(`${base}/${slug}/unpublish`);
      if (res.data.error) throw new Error(res.data.message || 'Failed to unpublish');
      return res.data.data;
    },
  };
}

// ─── Named exports (admin default) ───────────────────────────────────────────
// These preserve the existing import { getPage, saveDraft, ... } API so all
// current admin call sites continue to work with zero changes.

const _adminPbApi = makePageBuilderApi('/admin/editable-pages');

/**
 * GET /api/admin/editable-pages/:slug
 * Returns the full editing payload: { slug, title, status, layout, published_layout }
 */
export const getPage = (...args) => _adminPbApi.getPage(...args);

/**
 * PUT /api/admin/editable-pages/:slug
 * Saves a draft. Body: { title?, layout:Array }
 * Returns { slug, layout, status }
 * On 400 validation errors, throws an object with { message, errors:[] }
 */
export const saveDraft = (...args) => _adminPbApi.saveDraft(...args);

/**
 * POST /api/admin/editable-pages/:slug/publish
 * Returns { slug, status, published_at }
 */
export const publish = (...args) => _adminPbApi.publish(...args);

/**
 * POST /api/admin/editable-pages/:slug/unpublish
 * Returns { slug, status, published_layout: null }
 */
export const unpublish = (...args) => _adminPbApi.unpublish(...args);
